import { useEffect, useState, useRef } from "react";
import {
  Grid,
  Box,
  Button,
  VStack,
  HStack,
  Text,
  useBreakpointValue,
  Stack,
} from "@chakra-ui/react";
import * as Icon from "@phosphor-icons/react";
import ImageNavigator from "./ImgNavigator";
import PdCredits from "./PdCredits";
import SizeChart from "./SizeChart";
import { getProductDetails, getSimilarProducts } from "../../slices/product";
import { useSelector, useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import {
  addItemToCartServer,
  fetchCartFromServer,
  updateLocalCart,
} from "../../slices/cart";
import useDeviceType from "../helpers/GetDevice";
import AlertMe from "../helpers/AlertMe";
import { toast } from "react-toastify";
import CustomerReviews from "./CustomerReviews";
import {
  addItemToWishlistServer,
  fetchWishlistFromServer,
  removeItemFromWishlistServer,
  updateLocalWishlist,
} from "../../slices/wishlist";
import SessionService from "../../services/SessionService";
import Product from "./Product";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/free-mode";
import { Navigation, FreeMode, Autoplay } from "swiper/modules";

export default function ProductDetails() {
  const { id } = useParams();
  const dispatch = useDispatch();

  const { items } = useSelector((state) => state.cart);
  const { productData, similarProducts } = useSelector(
    (state) => state.product
  );
  const { items: wishlistItems } = useSelector((state) => state.wishlist);

  const [userLoggedIn] = useState(SessionService.isLoggedIn);
  const reviewsRef = useRef(null);
  const deviceType = useDeviceType();

  // Responsive grid layout
  const columnString = useBreakpointValue({
    base: "1fr",
    md: "1fr 1fr",
  });

  const colImages = useBreakpointValue({
    base: "1fr",
    sm: "1fr 1fr",
  });

  const [quantity, setQuantity] = useState(1);
  const [displayAlert, setDisplayAlert] = useState(false);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [selectedColorId, setSelectedColorId] = useState("");
  const [selectedSizeId, setSelectedSizeId] = useState("");
  const [productImages, setProductImages] = useState([]);
  const [product, setProduct] = useState({});
  const [zoomImage, setZoomImage] = useState(false);
  const [currentImgIndex, setCurrentImgIndex] = useState(0);
  const [openSizeGuide, setOpenSizeGuide] = useState(false);
  const [isAddedToCart, setIsAddedToCart] = useState(false);
  const [isAddedToWishlist, setIsAddedToWishlist] = useState(false);

  /* ---------------------- FETCH PRODUCT + SIMILAR ---------------------- */

  useEffect(() => {
    dispatch(getProductDetails({ slug: id }));
  }, [id, dispatch]);

  useEffect(() => {
    dispatch(getSimilarProducts({ slug: id }));
  }, [id, dispatch]);

  useEffect(() => {
    if (productData) {
      setProduct(productData);
      const initialColorId = productData.colors?.[0]?.id || "";
      setSelectedColorId(initialColorId);
      setSelectedSizeId("");
      setQuantity(productData.min_order_quantity || 1);
    }
  }, [productData]);

  useEffect(() => {
    if (productData && selectedColorId) {
      const initialVariant = productData.variants?.find(
        (v) => v.color.id === selectedColorId && v.in_stock && !v.is_disabled
      );

      setSelectedVariant(initialVariant || null);
      setSelectedSizeId(initialVariant?.size?.id || "");
      setProductImages(initialVariant?.images || productData?.images || []);
    }
  }, [productData, selectedColorId]);

  useEffect(() => {
    if (!product || !product.id || !selectedVariant) {
      if (quantity === 0) setQuantity(1);
      return;
    }

    const itemInCart =
      items &&
      items.data?.items?.find(
        (i) =>
          i.product.id === product.id && i.variant.id === selectedVariant.id
      );

    setQuantity(itemInCart ? itemInCart.quantity : quantity > 0 ? quantity : 1);
  }, [items, selectedVariant, product]);

  useEffect(() => {
    if (userLoggedIn) {
      dispatch(fetchCartFromServer());
    }
  }, [userLoggedIn, dispatch]);

  /* ---------------------- STOCK STATES ---------------------- */

  const isOutOfStock =
    !selectedVariant ||
    !selectedVariant.in_stock ||
    selectedVariant.is_disabled ||
    selectedVariant.current_stock <= 0;

  const allSizesSoldOutForColor =
    productData?.variants
      ?.filter((v) => v.color.id === selectedColorId)
      ?.every((v) => !v.in_stock || v.is_disabled || v.current_stock <= 0) ||
    false;

  const isSizeAvailable = (sizeId) => {
    if (!productData || !selectedColorId) return false;

    const variant = productData?.variants?.find(
      (v) => v.color.id === selectedColorId && v.size.id === sizeId
    );

    return (
      variant &&
      variant.in_stock &&
      !variant.is_disabled &&
      variant.current_stock > 0
    );
  };

  /* ---------------------- HANDLERS ---------------------- */

  const handleAddQty = () => {
    if (selectedVariant && quantity < selectedVariant.current_stock) {
      setQuantity(quantity + 1);
    }
  };

  const handleReduceQty = () => {
    if (quantity > 1) setQuantity(quantity - 1);
  };

  const handleAddToCart = () => {
    if (!selectedSizeId) {
      setDisplayAlert(true);
      toast.error("Please select a size");
      return;
    }

    if (!selectedVariant || !productData) {
      toast.error("Please select a valid variant (color and size)");
      return;
    }

    if (!selectedVariant.in_stock || selectedVariant.is_disabled) {
      toast.error("Selected item is currently out of stock.");
      return;
    }

    if (selectedVariant.current_stock < quantity) {
      toast.error(
        "Only " + selectedVariant.current_stock + " pieces available"
      );
      return;
    }

    const payload = {
      product_id: productData.id,
      variant_id: selectedVariant.id,
      size: selectedVariant.size.abbreviation,
      quantity: quantity > 0 ? quantity : 1,
    };

    const itemPayload = {
      product: productData,
      variant: selectedVariant,
      quantity: quantity > 0 ? quantity : 1,
    };

    if (userLoggedIn) {
      dispatch(addItemToCartServer(payload))
        .unwrap()
        .then(() => {
          toast.success("Product added to cart successfully!");
          dispatch(fetchCartFromServer());
        })
        .catch(() => toast.error("Failed to add product to cart."));
    } else {
      dispatch(updateLocalCart({ action: "ADD", itemPayload }));
      toast.success("Added to cart!");
    }
    setIsAddedToCart(true);
  };


useEffect(() => {
  if (productData && selectedColorId) {
    const initialVariant = productData.variants?.find(
      (v) => v.color.id === selectedColorId && v.in_stock && !v.is_disabled
    );

    setSelectedVariant(initialVariant || null);
    setSelectedSizeId(initialVariant?.size?.id || "");

    // FIX: ALWAYS show images even when out of stock
    const fallbackImages =
      initialVariant?.images?.length
        ? initialVariant.images
        : productData.images?.length
        ? productData.images
        : productData.primary_image
        ? [{ url: productData.primary_image }]
        : [];

    setProductImages(fallbackImages);
  }
}, [productData, selectedColorId]);


  const handleSizeChange = (sizeAbbreviation) => {
    if (allSizesSoldOutForColor) {
      toast.error("All sizes for this color are sold out");
      return;
    }

    const matchingVariant = productData.variants.find(
      (variant) =>
        variant.color.id === selectedColorId &&
        variant.size.abbreviation === sizeAbbreviation
    );

    if (
      matchingVariant &&
      matchingVariant.in_stock &&
      !matchingVariant.is_disabled &&
      matchingVariant.current_stock > 0
    ) {
      setSelectedVariant(matchingVariant);
      setSelectedSizeId(matchingVariant.size.id);
      setDisplayAlert(false);
    } else {
      setSelectedVariant(null);
      setSelectedSizeId("");
      toast.error("Selected size is currently unavailable for this color.");
    }
  };

  const handleColorChange = (colorId) => {
    setSelectedColorId(colorId);
    setSelectedVariant(null);
    setSelectedSizeId("");
    setProductImages(productData?.images || []);
    setDisplayAlert(false);
  };

  const handleWishlistToggle = (product) => {
    const isInWishlist = wishlistItems.some(
      (item) => item.product_id === product.id
    );
    setIsAddedToWishlist(true);

    if (userLoggedIn) {
      if (isInWishlist) {
        dispatch(removeItemFromWishlistServer(product.id))
          .unwrap()
          .then(() => toast.info("Removed from wishlist"))
          .catch(() => toast.error("Failed to remove from wishlist."));
      } else {
        dispatch(addItemToWishlistServer(product.id))
          .unwrap()
          .then(() => {
            toast.success("Added to wishlist");
            setTimeout(() => {
              dispatch(fetchWishlistFromServer());
            }, 500);
          })
          .catch(() => toast.error("Failed to add to wishlist."));
      }
    } else {
      dispatch(
        updateLocalWishlist({
          action: isInWishlist ? "REMOVE" : "ADD",
          product,
        })
      );
      toast.success(
        isInWishlist ? "Removed from wishlist" : "Added to wishlist"
      );
    }
  };

  const scrollToReviews = () =>
    reviewsRef.current?.scrollIntoView({ behavior: "smooth" });

  /* ---------------------- RENDER ---------------------- */

  return (
    <Box pt="90px" bg="var(--crt-9)">
      {zoomImage && (
        <ImageNavigator
          closeModal={() => setZoomImage(false)}
          images={productImages}
          index={currentImgIndex}
        />
      )}

      {openSizeGuide && (
        <SizeChart closeModal={() => setOpenSizeGuide(false)} data={product} />
      )}

      <Box p={{ base: 2, md: 4 }}>
        <Grid
          templateColumns={columnString}
          gap={{ base: 6, md: 8 }}
          mt="2"
          alignItems="flex-start"
        >
          {/* IMAGES */}
          <Grid
            templateColumns={colImages}
            gap="2"
            overflowY="auto"
            maxH={{ base: "auto", md: "535px" }}
            p={{ base: 2, md: 4 }}
            border="none"
          >
{productImages?.map((image, index) => (
  <Box
    key={index}
    onClick={() => {
      setCurrentImgIndex(index);
      setZoomImage(true);
    }}
  >
    <img
      src={image.url}
      alt={product.name}
      style={{
        borderRadius: "5px",
        width: "100%",
        objectFit: "contain",
      }}
    />
  </Box>
))}



          </Grid>

          {/* DETAILS */}
          <Box p={{ base: 2, md: 4 }} border="none">
            {/* Title */}
            <Box mb={3}>
              <Text fontSize="sm" color="gray.600">
                {productData?.categories?.[0]?.name || ""}
              </Text>
              <Text fontSize={{ base: "xl", md: "2xl" }} fontWeight="bold">
                {productData.name}
                {productData?.reviews_count !== undefined && (
                  <Text
                    as="span"
                    ml={2}
                    fontSize="sm"
                    fontWeight="normal"
                    color="teal.500"
                    cursor="pointer"
                    onClick={scrollToReviews}
                    _hover={{ textDecoration: "underline" }}
                  >
                    ({productData.reviews_count} Reviews)
                  </Text>
                )}
              </Text>
            </Box>

            {/* Pricing */}
            <HStack flexWrap="wrap" spacing={3} mb={3}>
              <Text fontSize="xl" fontWeight="bold">
                ₹
                {selectedVariant?.display_price?.toFixed(2) ||
                  productData?.list_price?.toFixed(2)}
              </Text>
              <Text
                style={{
                  textDecorationLine: "line-through",
                  textDecoration: "italic",
                }}
              >
                ₹
                {productData?.orignial_price?.toFixed(2) ||
                  productData?.sale_price?.toFixed(2)}
              </Text>
              <Text color="red.500" fontWeight="semibold">
                {(() => {
                  const variant = selectedVariant;
                  const original = variant?.orignial_price || 0;
                  const current = variant?.display_price || 0;
                  if (!original || original <= 0) return 0;
                  const discountPercent = Math.round(
                    ((original - current) / original) * 100
                  );
                  return discountPercent;
                })()}
                % OFF
              </Text>
            </HStack>

            {/* Variant Out of Stock Banner */}
            {isOutOfStock && (
              <Box
                bg="red.50"
                border="1px solid #E53E3E"
                borderRadius="md"
                p={3}
                mb={4}
                textAlign="center"
              >
                <Text color="red.600" fontWeight="bold" fontSize="lg">
                  OUT OF STOCK
                </Text>
                <Text fontSize="sm" color="gray.600">
                  Try selecting a different size or color.
                </Text>
              </Box>
            )}

            {/* Urgency badge */}
            {selectedVariant &&
              selectedVariant.current_stock > 0 &&
              selectedVariant.current_stock <= 3 &&
              !isOutOfStock && (
                <Box
                  bg="orange.50"
                  border="1px solid #DD6B20"
                  p={2}
                  borderRadius="md"
                  mb={3}
                >
                  <Text color="orange.600" fontWeight="bold" mt={5}>
                    Only {selectedVariant.current_stock} left in stock!
                  </Text>
                </Box>
              )}

            {/* Description */}
            <Text mb={3} color="gray.700">
              {productData?.description}
            </Text>

            {/* Colors */}
            <Box mb={3}>
              <Text fontWeight="medium">Colors:</Text>
              <HStack spacing={3} mt={2} flexWrap="wrap">
                {productData?.colors?.map((color) => {
                  const colorVariants =
                    productData?.variants?.filter(
                      (v) => v.color?.id === color.id
                    ) || [];

                  if (colorVariants.length === 0) return null;

                  const isAvailable = colorVariants.some(
                    (v) => v.in_stock && !v.is_disabled && v.current_stock > 0
                  );

                  const colorImage =
                    colorVariants.find((v) => v.thumb_images?.length > 0)
                      ?.thumb_images[0]?.url || "/images/placeholder.jpg";

                  return (
                    <img
                      key={color.id}
                      onClick={() => isAvailable && handleColorChange(color.id)}
                      src={colorImage}
                      alt={color.name || "Color variant"}
                      style={{
                        borderRadius: "6px",
                        width: "70px",
                        height: "100px",
                        objectFit: "contain",
                        border:
                          selectedColorId === color.id
                            ? "2px solid #E53E3E"
                            : isAvailable
                            ? "1px solid #E2E8F0"
                            : "1px solid #CBD5E0",
                        opacity: isAvailable ? 1 : 0.5,
                        cursor: isAvailable ? "pointer" : "not-allowed",
                      }}
                    />
                  );
                })}
              </HStack>
            </Box>

            <Box mb={4}>
              <HStack justify="space-between">
                <Text fontWeight="medium">Sizes:</Text>
                <Text
                  color="teal.500"
                  textDecoration="underline"
                  cursor="pointer"
                  onClick={() => setOpenSizeGuide(true)}
                >
                  Size Guide
                </Text>
              </HStack>

              {allSizesSoldOutForColor && (
                <Box
                  bg="red.50"
                  border="1px solid #E53E3E"
                  borderRadius="md"
                  p={3}
                  mt={3}
                  mb={4}
                  textAlign="center"
                >
                  <Text color="red.600" fontWeight="bold">
                    All sizes for this color are sold out
                  </Text>
                  <Text fontSize="sm" color="gray.600">
                    Please try a different color.
                  </Text>
                </Box>
              )}

              <HStack mt={2} spacing={3} flexWrap="wrap">
                {productData?.sizes?.map((size) => {
                  const baseAvailable = isSizeAvailable(size.id);
                  const isClickable = !allSizesSoldOutForColor && baseAvailable;
                  const isActive =
                    selectedVariant &&
                    selectedVariant.size.id === size.id &&
                    !allSizesSoldOutForColor;

                  return (
                    <Box
                      key={size.id}
                      onClick={() =>
                        isClickable && handleSizeChange(size.abbreviation)
                      }
                      style={{
                        padding: "8px 12px",
                        borderRadius: "4px",
                        border: "1px solid #ccc",
                        fontWeight: "bold",
                        cursor: isClickable ? "pointer" : "not-allowed",
                        opacity: isClickable ? 1 : 0.3,
                        background: isActive ? "#E53E3E" : "white",
                        color: isActive ? "white" : "black",
                        position: "relative",
                        transition: "0.2s",
                      }}
                    >
                      {size.abbreviation}
                      {/* {!baseAvailable && !allSizesSoldOutForColor && (
                        <span
                          style={{
                            fontSize: "10px",
                            color: "red",
                            position: "absolute",
                            bottom: "-14px",
                            left: "0",
                          }}
                        >
                          OUT OF STOCK
                        </span>
                      )} */}
                    </Box>
                  );
                })}
              </HStack>

              <AlertMe
                mt={2}
                variant="outline"
                showAlert={displayAlert}
                close={() => setDisplayAlert(false)}
                status="error"
                message="Please select a size"
                header="Select Size"
              />
            </Box>

            {/* Quantity & Add to Cart */}
            <VStack align="stretch" spacing={4}>
              <HStack justify="space-between">
                <Text fontWeight="bold">Quantity:</Text>
                {selectedVariant && (
                  <Text fontSize="sm" color="green.600" fontWeight="medium">
                    Available: {selectedVariant.current_stock} pieces
                  </Text>
                )}
              </HStack>
       {productData.min_order_quantity > 1 && ( <span style={{ color: "red", fontSize: "12px", marginTop: "-15px", fontStyle: "italic", fontWeight: "500", }} > {`You must add at least ${productData.min_order_quantity} pieces of any color/size to place this order`} </span> )} 

              <Stack
                direction={{ base: "column", sm: "row" }}
                spacing={4}
                align="flex-start"
              >
                <HStack
                  border="1px solid"
                  borderColor="gray.300"
                  borderRadius="md"
                  overflow="hidden"
                  w={{ base: "100%", sm: "120px" }}
                  justify="space-between"
                >
                  <Button
                    onClick={quantity > 1 ? handleReduceQty : undefined}
                    disabled={
                      quantity <= (productData?.min_order_quantity || 1)
                    }
                    variant="ghost"
                    colorScheme="gray"
                  >
                    -
                  </Button>
                  <Text fontWeight="bold" pt={3}>
                    {quantity}
                  </Text>
                  <Button
                    onClick={handleAddQty}
                    variant="ghost"
                    colorScheme="gray"
                    disabled={
                      !selectedVariant ||
                      selectedVariant.current_stock <= quantity
                    }
                  >
                    +
                  </Button>
                </HStack>

                <Button
                  bg={isOutOfStock ? "gray.400" : "#E53E3E"}
                  size="lg"
                  leftIcon={<Icon.ShoppingCart size={20} color="#f8f8f8" />}
                  onClick={!isOutOfStock ? handleAddToCart : undefined}
                  width={{ base: "100%", sm: "300px" }}
                  _hover={{ opacity: !isOutOfStock ? 0.9 : 1 }}
                  disabled={isOutOfStock}
                >
                  {isOutOfStock ? "Out of Stock" : "Add to Cart"}
                </Button>

                {userLoggedIn && (
                  <Button
                    variant="outline"
                    colorScheme="teal"
                    size="lg"
                    width={{ base: "100%", sm: "300px" }}
                    onClick={() => handleWishlistToggle(productData)}
                  >
                    <Icon.Heart
                      size={20}
                      color={
                        isAddedToWishlist ? "#E53E3E" : "var(--cr-red-logo)"
                      }
                      weight={isAddedToWishlist ? "fill" : "regular"}
                    />{" "}
                    {isAddedToWishlist
                      ? "Added to Wishlist"
                      : "Add to Wishlist"}
                  </Button>
                )}
              
              </Stack>
            </VStack>
          </Box>
        </Grid>
      </Box>

      {/* Similar Products */}
      {similarProducts?.filter((p) => p.id !== productData.id).length > 0 && (
        <Box textAlign="center" py={10}>
          <Text fontSize="2xl" fontWeight="bold">
            Similar Products
          </Text>
          <Text color="gray.500" mb={5}>
            Products you may also like
          </Text>

          <Swiper
            modules={[Navigation, FreeMode, Autoplay]}
            freeMode={true}
            spaceBetween={15}
            slidesPerView={deviceType === "desktop" ? 6 : 1}
            autoplay={{ delay: 2500 }}
            loop={true}
            navigation
            breakpoints={{
              480: { slidesPerView: 2.2 },
              768: { slidesPerView: 3.2 },
              1024: { slidesPerView: 4.2 },
              1280: { slidesPerView: 5.2 },
            }}
            style={{ padding: "0 10px" }}
          >
            {similarProducts
              ?.filter((p) => p.id !== productData.id)
              ?.map((product) => (
                <SwiperSlide key={product.id}>
                  <Product
                    isSimilar
                    product={{
                      id: product.id,
                      name: product.name,
                      slug: product.slug,
                      primary_image: product.primary_image,
                      display_price: product.display_price,
                      list_price: product.original_price,
                      thumb_images: [product.primary_image],
                      original_price: product.original_price,
                    }}
                  />
                </SwiperSlide>
              ))}
          </Swiper>
        </Box>
      )}

      {/* Customer Reviews */}
      <div ref={reviewsRef}>
        <CustomerReviews productId={productData.id} />
      </div>
    </Box>
  );
}
